import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface SubtopicScore {
    subtopic: string;
    score: number;
}

interface Participant {
    rank?: number | string;
    name: string;
    designation: string;
    avgScore: number;
    totalQuizzes: number;
    subtopicScores?: SubtopicScore[];
}

/**
 * Generates and downloads a styled leaderboard PDF.
 * @param participants - Full sorted list of all participants
 * @param subtopicHeaders  - Unique subtopic names to include as score columns
 */
const generateLeaderboardPDF = (participants: Participant[], subtopicHeaders: string[] = []) => {
  try {
    console.log("Starting PDF generation for", participants.length, "participants");
    
    // Create new document
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });

    // ── Header band ─────────────────────────────────────────────────────────
    doc.setFillColor(94, 53, 177); // Purple
    doc.rect(0, 0, pageWidth, 60, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text('Leaderboard Report', 40, 36);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(220, 210, 255);
    doc.text(`Generated on ${dateStr}`, 40, 52);

    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`Total Participants: ${participants.length}`, pageWidth - 40, 36, { align: 'right' });

    // ── Top 3 callout boxes (if any) ─────────────────────────────────────────
    if (participants && participants.length > 0) {
      const top3 = participants.slice(0, 3);
      const medalsLabel = ['1st', '2nd', '3rd'];
      const boxW = (pageWidth - 80) / 3;

      top3.forEach((user, i) => {
        const x = 40 + i * boxW;
        doc.setFillColor(248, 246, 255);
        doc.roundedRect(x, 70, boxW - 4, 52, 5, 5, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(94, 53, 177);
        doc.text(`${medalsLabel[i]} Place`, x + 10, 90);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(30, 30, 30);
        const nameText = user.name || "N/A";
        const truncatedName = nameText.length > 22 ? nameText.substring(0, 20) + '...' : nameText;
        doc.text(truncatedName, x + 10, 106);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text(user.designation || '-', x + 10, 118);

        // Score bubble
        doc.setFillColor(94, 53, 177);
        doc.roundedRect(x + boxW - 55, 75, 48, 22, 11, 11, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(`${user.avgScore || 0}%`, x + boxW - 31, 90, { align: 'center' });
      });
    }

    // ── Table ────────────────────────────────────────────────────────────────
    const subtopicCols = (subtopicHeaders || []).slice(0, 4);
    const head = [['Rank', 'Name', 'Designation', 'Avg Score', 'Total Quizzes', ...subtopicCols]];

    const body = (participants || []).map((user) => {
      const subtopicValues = subtopicCols.map((header) => {
        const ts = (user.subtopicScores || []).find((t) => t.subtopic === header);
        return ts ? `${ts.score}%` : '-';
      });
      return [
        user.rank || '-',
        user.name || '',
        user.designation || '-',
        `${user.avgScore || 0}%`,
        user.totalQuizzes || 0,
        ...subtopicValues,
      ];
    });

    const accentColors = [
      [255, 200, 0],   // gold
      [170, 170, 170], // silver
      [180, 100, 40],  // bronze
    ];

    // Using the imported autoTable function directly
    autoTable(doc, {
      startY: 132,
      head: head,
      body: body,
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 7,
        overflow: 'linebreak',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [94, 53, 177],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [248, 246, 255],
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 36 },
        1: { cellWidth: 120 },
        2: { cellWidth: 100 },
        3: { halign: 'center', cellWidth: 65 },
        4: { halign: 'center', cellWidth: 75 },
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0 && data.row.index < 3) {
          const c = accentColors[data.row.index];
          if (c) {
            doc.setFillColor(c[0], c[1], c[2]);
            doc.rect(data.cell.x, data.cell.y, 3, data.cell.height, 'F');
          }
        }
      },
      margin: { left: 40, right: 40 },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageH - 10, { align: 'center' });
      doc.text('Railway Quiz - Confidential', 40, pageH - 10);
    }

    const fileName = `leaderboard-report-${now.toISOString().split('T')[0]}.pdf`;
    console.log("Saving PDF as:", fileName);
    doc.save(fileName);
    console.log("PDF save command executed.");
  } catch (err) {
    console.error("CRITICAL PDF ERROR:", err);
  }
};

export default generateLeaderboardPDF;
